// The value chain's state in the address: what the page shows is read from
// the query string and written back to it, so that a copied address shows the
// same chain. The module is pure: it knows nothing of the router or of React.

import { ValueChainParams } from "../../services/productPlaneApi";
import { PlaneEntityType } from "../../types/productPlane";
import {
  ChainCardType,
  ChainDirection,
  ChainSide,
  ClassFold,
  FlagRule,
} from "../../types/valueChain";

// What the cards say of each kind: its makers, the split by country, or the
// facilities. A lens restyles the cards; it never changes which are drawn.
export type ChainLens = "makers" | "jurisdiction" | "facilities";

// How the chain is laid out: the cards on a canvas in the columns of the
// read, by tier and area (grouped in each tier's column, or as a matrix of
// areas by tier), or as a tree of subsystems opened one branch at a time.
// Being tried side by side; one will stay.
export type ChainView = "graph" | "tree" | "grouped" | "matrix";

// The root, or an opened card: a kind or a class by its id
export interface ChainCardRef {
  type: ChainCardType;
  id: string;
}

// The type of an entity whose detail is open: one of the product plane, or a
// kind or class of the chain
export type ChainDetailType = PlaneEntityType | ChainCardType;

// The entity whose detail is open
export interface ChainDetailRef {
  type: ChainDetailType;
  id: string;
}

export interface ValueChainState {
  // The root of the chain; null until one is named
  root: ChainCardRef | null;
  direction: ChainDirection;
  // Steps read towards the parts
  partsDepth: number;
  // Steps read towards the hosts, when the direction reads them
  hostsDepth: number;
  lens: ChainLens;
  // The cards opened beyond the read of the root, in the order they were
  // opened: each is read on its own with the card as root
  opened: ChainCardRef[];
  // The entity whose detail is open
  detail: ChainDetailRef | null;
  // The organization whose own paths are highlighted, by its id
  maker: string | null;
  // With both directions read, whether the parts side is drawn as one stub
  // card or opened into its columns
  partsStub: PartsStub;
  // The jurisdiction lens's home jurisdiction, a country code in capitals;
  // null until one is set
  home: string | null;
  // The countries the analyst trusts beside the home one, in capitals, in
  // the order they were added: a bucket of their own, never domestic
  trusted: string[];
  // When a kind is flagged: no domestic maker on record, or fewer than two
  rule: FlagRule;
  // Whether product mentions that carry no name are read
  unnamed: boolean;
  // Which classes a product stands on: its primary class alone, or every
  // class it is filed under
  classes: ClassFold;
  // Whether the typical-host rows are read as edges beside the stated pairs
  typical: boolean;
  // How the chain is laid out
  view: ChainView;
  // The tree view's open subsystem, by its name; null when all are closed
  branch: string | null;
  // The kinds open in it, from its first level down, each inside the one
  // before
  trail: string[];
}

// The parts side of the focus view: one stub card, or its columns
export type PartsStub = "collapsed" | "open";

export const PARTS_STUBS: readonly PartsStub[] = ["collapsed", "open"];

export const CHAIN_DIRECTIONS: readonly ChainDirection[] = [
  "hosts",
  "both",
  "parts",
];

// The directions the page offers: the tree reads one side at a time, its
// parts (the composition) or what it is built into (the integration). Both
// sides at once stay readable by address, for the graph.
export const ACTIVE_DIRECTIONS: readonly ChainDirection[] = ["parts", "hosts"];

export const CHAIN_LENSES: readonly ChainLens[] = [
  "makers",
  "jurisdiction",
  "facilities",
];

// The lenses the page draws
export const ACTIVE_LENSES: readonly ChainLens[] = [
  "makers",
  "jurisdiction",
  "facilities",
];

export const FLAG_RULES: readonly FlagRule[] = [
  "none_domestic",
  "fewer_than_two",
];

export const CLASS_FOLDS: readonly ClassFold[] = ["primary", "every"];

export const CHAIN_VIEWS: readonly ChainView[] = [
  "graph",
  "tree",
  "grouped",
  "matrix",
];

// The layouts the page offers. The others cannot draw a chain of the size
// the graph holds; they are kept, and readable by address alone.
export const ACTIVE_VIEWS: readonly ChainView[] = ["tree"];

// A country code as the backend takes it: two or three letters
const COUNTRY_CODE = /^[A-Za-z]{2,3}$/;

// A country code in capitals, or null when the value is not one
export const toCountryCode = (value: string | null | undefined): string | null => {
  const code = (value ?? "").trim();
  return COUNTRY_CODE.test(code) ? code.toUpperCase() : null;
};

// Between the codes of the trusted set in one parameter
const LIST_SEPARATOR = ",";

const CARD_TYPES: readonly ChainCardType[] = ["kind", "class"];

export const DETAIL_TYPES: readonly ChainDetailType[] = [
  "organization",
  "facility",
  "project",
  "source",
  "product",
  "category",
  "capability",
  "capability_part",
  "incident",
  "kind",
  "class",
];

// Steps a request may read from the root in one direction
export const MIN_CHAIN_DEPTH = 1;
export const MAX_CHAIN_DEPTH = 6;
export const DEFAULT_CHAIN_DEPTH = 2;

// The direction a root is read in when the address names none: its parts,
// whatever the root. The tree reads one side at a time, and what a root is
// built into is one press away.
export const defaultDirection = (
  _root: ChainCardRef | null,
): ChainDirection => "parts";

// The state of an address without parameters. A part equal to its default
// is left out of the address. The page reads wider than the backend does by
// default: unnamed mentions, every class of a product and the typical hosts.
export const DEFAULT_VALUE_CHAIN_STATE: ValueChainState = {
  root: null,
  direction: "parts",
  partsDepth: DEFAULT_CHAIN_DEPTH,
  hostsDepth: DEFAULT_CHAIN_DEPTH,
  lens: "makers",
  opened: [],
  detail: null,
  maker: null,
  partsStub: "collapsed",
  home: null,
  trusted: [],
  rule: "none_domestic",
  unnamed: true,
  classes: "every",
  typical: true,
  view: "tree",
  branch: null,
  trail: [],
};

// The query parameter of each part of the state; the root takes two
export const VALUE_CHAIN_PARAMS = {
  root: "root",
  rootType: "type",
  direction: "dir",
  partsDepth: "parts",
  hostsDepth: "hosts",
  lens: "lens",
  opened: "open",
  detail: "detail",
  maker: "maker",
  partsStub: "stub",
  home: "home",
  trusted: "trusted",
  rule: "rule",
  unnamed: "unnamed",
  classes: "classes",
  typical: "typical",
  view: "view",
  branch: "branch",
  trail: "trail",
} as const;

// Between the type and the id of a reference in one parameter. Ids may hold
// it too: only the first one separates.
const SEPARATOR = ":";

const WHOLE_NUMBER = /^\d+$/;

const oneOf = <T extends string>(
  allowed: readonly T[],
  value: string | null,
  fallback: T,
): T => (allowed.includes(value as T) ? (value as T) : fallback);

// A switch as the address writes it: 1 on, 0 off
const SWITCH_ON = "1";
const SWITCH_OFF = "0";

// A switch from the address; anything but 1 or 0 is its default
const decodeSwitch = (value: string | null, fallback: boolean): boolean =>
  value === SWITCH_ON ? true : value === SWITCH_OFF ? false : fallback;

const decodeDepth = (value: string | null): number => {
  if (!value || !WHOLE_NUMBER.test(value)) {
    return DEFAULT_CHAIN_DEPTH;
  }
  const depth = Number(value);
  return depth >= MIN_CHAIN_DEPTH && depth <= MAX_CHAIN_DEPTH
    ? depth
    : DEFAULT_CHAIN_DEPTH;
};

// A `type:id` reference, or null when it is not one of the allowed types
const decodeRef = <T extends string>(
  value: string | null,
  types: readonly T[],
): { type: T; id: string } | null => {
  if (!value) {
    return null;
  }
  const at = value.indexOf(SEPARATOR);
  if (at < 0) {
    return null;
  }
  const type = value.slice(0, at);
  const id = value.slice(at + 1);
  return id && types.includes(type as T) ? { type: type as T, id } : null;
};

const encodeRef = (ref: { type: string; id: string }): string =>
  `${ref.type}${SEPARATOR}${ref.id}`;

// The trusted set: country codes in capitals, each once, in the given order.
// What is not a country code is left out.
export const toTrustedSet = (values: readonly string[]): string[] =>
  Array.from(
    new Set(
      values
        .map(toCountryCode)
        .filter((code): code is string => code !== null),
    ),
  );

const decodeTrusted = (value: string | null): string[] =>
  toTrustedSet((value ?? "").split(LIST_SEPARATOR));

const decodeRoot = (params: URLSearchParams): ChainCardRef | null => {
  const id = (params.get(VALUE_CHAIN_PARAMS.root) ?? "").trim();
  if (!id) {
    return null;
  }
  return {
    type: oneOf(CARD_TYPES, params.get(VALUE_CHAIN_PARAMS.rootType), "kind"),
    id,
  };
};

// Reads the state from an address. A missing or unknown value is its default.
export const decodeValueChainState = (
  params: URLSearchParams,
): ValueChainState => {
  const root = decodeRoot(params);
  return {
    root,
    direction: oneOf(
      CHAIN_DIRECTIONS,
      params.get(VALUE_CHAIN_PARAMS.direction),
      defaultDirection(root),
    ),
    partsDepth: decodeDepth(params.get(VALUE_CHAIN_PARAMS.partsDepth)),
    hostsDepth: decodeDepth(params.get(VALUE_CHAIN_PARAMS.hostsDepth)),
    lens: oneOf(
      CHAIN_LENSES,
      params.get(VALUE_CHAIN_PARAMS.lens),
      DEFAULT_VALUE_CHAIN_STATE.lens,
    ),
    opened: params
      .getAll(VALUE_CHAIN_PARAMS.opened)
      .map((value) => decodeRef(value, CARD_TYPES))
      .filter((ref): ref is ChainCardRef => ref !== null),
    detail: decodeRef(params.get(VALUE_CHAIN_PARAMS.detail), DETAIL_TYPES),
    maker: params.get(VALUE_CHAIN_PARAMS.maker) || null,
    partsStub: oneOf(
      PARTS_STUBS,
      params.get(VALUE_CHAIN_PARAMS.partsStub),
      DEFAULT_VALUE_CHAIN_STATE.partsStub,
    ),
    home: toCountryCode(params.get(VALUE_CHAIN_PARAMS.home)),
    trusted: decodeTrusted(params.get(VALUE_CHAIN_PARAMS.trusted)),
    rule: oneOf(
      FLAG_RULES,
      params.get(VALUE_CHAIN_PARAMS.rule),
      DEFAULT_VALUE_CHAIN_STATE.rule,
    ),
    unnamed: decodeSwitch(
      params.get(VALUE_CHAIN_PARAMS.unnamed),
      DEFAULT_VALUE_CHAIN_STATE.unnamed,
    ),
    classes: oneOf(
      CLASS_FOLDS,
      params.get(VALUE_CHAIN_PARAMS.classes),
      DEFAULT_VALUE_CHAIN_STATE.classes,
    ),
    typical: decodeSwitch(
      params.get(VALUE_CHAIN_PARAMS.typical),
      DEFAULT_VALUE_CHAIN_STATE.typical,
    ),
    view: oneOf(
      CHAIN_VIEWS,
      params.get(VALUE_CHAIN_PARAMS.view),
      DEFAULT_VALUE_CHAIN_STATE.view,
    ),
    branch: params.get(VALUE_CHAIN_PARAMS.branch) || null,
    trail: params.getAll(VALUE_CHAIN_PARAMS.trail).filter((id) => id !== ""),
  };
};

// Writes the state into an address. The parameters of `base` that are not
// the value chain's own are kept; `base` itself is not changed.
export const encodeValueChainState = (
  state: ValueChainState,
  base?: URLSearchParams,
): URLSearchParams => {
  const params = new URLSearchParams(base);
  for (const name of Object.values(VALUE_CHAIN_PARAMS)) {
    params.delete(name);
  }

  if (state.root) {
    params.set(VALUE_CHAIN_PARAMS.root, state.root.id);
    if (state.root.type !== "kind") {
      params.set(VALUE_CHAIN_PARAMS.rootType, state.root.type);
    }
  }
  if (state.direction !== defaultDirection(state.root)) {
    params.set(VALUE_CHAIN_PARAMS.direction, state.direction);
  }
  if (state.partsDepth !== DEFAULT_CHAIN_DEPTH) {
    params.set(VALUE_CHAIN_PARAMS.partsDepth, String(state.partsDepth));
  }
  if (state.hostsDepth !== DEFAULT_CHAIN_DEPTH) {
    params.set(VALUE_CHAIN_PARAMS.hostsDepth, String(state.hostsDepth));
  }
  if (state.lens !== DEFAULT_VALUE_CHAIN_STATE.lens) {
    params.set(VALUE_CHAIN_PARAMS.lens, state.lens);
  }
  for (const card of state.opened) {
    params.append(VALUE_CHAIN_PARAMS.opened, encodeRef(card));
  }
  if (state.detail) {
    params.set(VALUE_CHAIN_PARAMS.detail, encodeRef(state.detail));
  }
  if (state.maker) {
    params.set(VALUE_CHAIN_PARAMS.maker, state.maker);
  }
  if (state.partsStub !== DEFAULT_VALUE_CHAIN_STATE.partsStub) {
    params.set(VALUE_CHAIN_PARAMS.partsStub, state.partsStub);
  }
  if (state.home) {
    params.set(VALUE_CHAIN_PARAMS.home, state.home);
  }
  if (state.trusted.length > 0) {
    params.set(VALUE_CHAIN_PARAMS.trusted, state.trusted.join(LIST_SEPARATOR));
  }
  if (state.rule !== DEFAULT_VALUE_CHAIN_STATE.rule) {
    params.set(VALUE_CHAIN_PARAMS.rule, state.rule);
  }
  if (state.unnamed !== DEFAULT_VALUE_CHAIN_STATE.unnamed) {
    params.set(
      VALUE_CHAIN_PARAMS.unnamed,
      state.unnamed ? SWITCH_ON : SWITCH_OFF,
    );
  }
  if (state.classes !== DEFAULT_VALUE_CHAIN_STATE.classes) {
    params.set(VALUE_CHAIN_PARAMS.classes, state.classes);
  }
  if (state.typical !== DEFAULT_VALUE_CHAIN_STATE.typical) {
    params.set(
      VALUE_CHAIN_PARAMS.typical,
      state.typical ? SWITCH_ON : SWITCH_OFF,
    );
  }
  if (state.view !== DEFAULT_VALUE_CHAIN_STATE.view) {
    params.set(VALUE_CHAIN_PARAMS.view, state.view);
  }
  if (state.branch) {
    params.set(VALUE_CHAIN_PARAMS.branch, state.branch);
  }
  for (const id of state.trail) {
    params.append(VALUE_CHAIN_PARAMS.trail, id);
  }
  return params;
};

const sameRef = (a: ChainCardRef | null, b: ChainCardRef | null): boolean =>
  a?.type === b?.type && a?.id === b?.id;

// The state after a change. A change of root resets the opened cards, the
// highlighted maker, the parts stub and the tree's open branch, and reads the
// new root in its default direction, unless the change names them. The lens,
// the jurisdiction settings and the read switches are kept. In the tree, a
// change of direction draws another tree: its open branch and the cards
// opened in it are reset too, unless the change names them.
export const changeValueChainState = (
  state: ValueChainState,
  change: Partial<ValueChainState>,
): ValueChainState => {
  const rootChanged = "root" in change && !sameRef(state.root, change.root ?? null);
  if (!rootChanged) {
    const treeTurned =
      (change.view ?? state.view) === "tree" &&
      change.direction !== undefined &&
      change.direction !== state.direction;
    return treeTurned
      ? { ...state, opened: [], branch: null, trail: [], ...change }
      : { ...state, ...change };
  }
  return {
    ...state,
    opened: [],
    maker: null,
    partsStub: DEFAULT_VALUE_CHAIN_STATE.partsStub,
    branch: null,
    trail: [],
    direction: defaultDirection(change.root ?? null),
    ...change,
  };
};

// Whether a card is among the opened ones
export const isOpened = (
  opened: readonly ChainCardRef[],
  card: ChainCardRef,
): boolean => opened.some((ref) => sameRef(ref, card));

// The opened cards after a card's expander is used: opened if it was closed,
// closed if it was open
export const toggleOpened = (
  opened: readonly ChainCardRef[],
  card: ChainCardRef,
): ChainCardRef[] =>
  isOpened(opened, card)
    ? opened.filter((ref) => !sameRef(ref, card))
    : [...opened, { type: card.type, id: card.id }];

// The jurisdiction part of a request: asked for only under the jurisdiction
// lens with a home jurisdiction set, so that the other lenses share one read.
// A part equal to the backend's default is left out.
export type JurisdictionParams = Pick<
  ValueChainParams,
  "home_jurisdiction" | "flag_rule" | "trusted_jurisdictions"
>;

export const jurisdictionParams = (
  state: Pick<ValueChainState, "lens" | "home" | "trusted" | "rule">,
): JurisdictionParams => {
  if (state.lens !== "jurisdiction" || !state.home) {
    return {};
  }
  return {
    home_jurisdiction: state.home,
    ...(state.rule !== DEFAULT_VALUE_CHAIN_STATE.rule
      ? { flag_rule: state.rule }
      : {}),
    ...(state.trusted.length > 0
      ? { trusted_jurisdictions: state.trusted }
      : {}),
  };
};

// The switches of a request: what is read beyond the stated pairs of named
// products on their primary class. A switch at the backend's default is
// left out.
export type SwitchParams = Pick<
  ValueChainParams,
  "include_unnamed" | "classes" | "typical_hosts"
>;

export const switchParams = (
  state: Pick<ValueChainState, "unnamed" | "classes" | "typical">,
): SwitchParams => ({
  ...(state.unnamed ? { include_unnamed: true } : {}),
  ...(state.classes === "every" ? { classes: state.classes } : {}),
  ...(state.typical ? { typical_hosts: true } : {}),
});

// What every read of the page shares, the opened cards' included: the
// jurisdiction part and the switches
export type ReadParams = JurisdictionParams & SwitchParams;

export const readParams = (
  state: Pick<
    ValueChainState,
    "lens" | "home" | "trusted" | "rule" | "unnamed" | "classes" | "typical"
  >,
): ReadParams => ({
  ...jurisdictionParams(state),
  ...switchParams(state),
});

// The request of the chain for a state; null without a root
export const toValueChainParams = (
  state: ValueChainState,
): ValueChainParams | null => {
  if (!state.root) {
    return null;
  }
  return {
    root_type: state.root.type,
    root_id: state.root.id,
    direction: state.direction,
    parts_depth: state.partsDepth,
    ...(state.direction === "parts" ? {} : { hosts_depth: state.hostsDepth }),
    ...readParams(state),
  };
};

// The request that opens a card: the same read with the card as root, one
// step deep, under the same jurisdiction settings and switches; towards its
// parts, or, in the tree of what the root is built into, its own hosts
export const expansionParams = (
  card: ChainCardRef,
  read: ReadParams = {},
  side: ChainSide = "parts",
): ValueChainParams => ({
  root_type: card.type,
  root_id: card.id,
  direction: side,
  parts_depth: 1,
  ...(side === "hosts" ? { hosts_depth: 1 } : {}),
  ...read,
});
